import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { validationRegExpr } from "@shared/constants/validations-regexp";
import { IUserService } from "@shared/interfaces/user.interface";
import { HttpErrorResponse } from "@shared/models/error/http-error-response";
import { UserProviderType } from "@shared/types/user/enum/user-provider-type.enum";
import { ManageProviderDto, UserDto } from "@shared/types/user/user.dto";
import { Alert, Button, Form, Input, Modal, Select, Space } from "antd";
import { useEffect } from "react";
import { useMutation } from "react-query";

type AssignableProvider = ManageProviderDto["provider"];

function getManageProviderErrorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return (
      error.findFirstErrorMessage() ||
      "Failed to update provider. Please try again."
    );
  }
  return "Failed to update provider. Please try again.";
}

const EMAIL_PROVIDERS: { label: string; value: AssignableProvider }[] = [
  { label: "Google", value: UserProviderType.GOOGLE },
  { label: "Apple", value: UserProviderType.APPLE },
];

interface ManageProviderModalProps {
  open: boolean;
  user: UserDto | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ManageProviderModal = ({
  open,
  user,
  onClose,
  onSuccess,
}: ManageProviderModalProps) => {
  const [form] = Form.useForm<ManageProviderDto>();
  const usersService = appInject<IUserService>(DI_TOKENS.userService);

  const current = user?.provider;
  const providerOptions: { label: string; value: AssignableProvider }[] =
    current === UserProviderType.PHONE
      ? EMAIL_PROVIDERS
      : current === UserProviderType.GOOGLE ||
          current === UserProviderType.APPLE
        ? [{ label: "Phone", value: UserProviderType.PHONE }]
        : [...EMAIL_PROVIDERS, { label: "Phone", value: UserProviderType.PHONE }];

  const selectedProvider = Form.useWatch("provider", form);
  const isEmailProvider =
    selectedProvider === UserProviderType.GOOGLE ||
    selectedProvider === UserProviderType.APPLE;

  const manageProviderMutation = useMutation(
    (dto: ManageProviderDto) => usersService.manageProvider(dto),
    {
      onSuccess: () => {
        form.resetFields();
        manageProviderMutation.reset();
        onSuccess();
        onClose();
      },
    },
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    form.resetFields();

    if (
      current === UserProviderType.GOOGLE ||
      current === UserProviderType.APPLE
    ) {
      form.setFieldsValue({ provider: UserProviderType.PHONE });
    }
  }, [open, form, current]);

  const handleClose = () => {
    form.resetFields();
    manageProviderMutation.reset();
    onClose();
  };

  const handleFinish = (values: ManageProviderDto) => {
    if (!user?.id) {
      return;
    }

    manageProviderMutation.mutate({
      id: user.id,
      provider: values.provider,
      email: values.email,
      phoneNumber: values.phoneNumber,
      providerId: values.providerId?.trim() || undefined,
    });
  };

  return (
    <Modal
      title="Manage Provider"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={handleFinish}
      >
        {manageProviderMutation.isError && (
          <Form.Item>
            <Alert
              message="Error"
              description={getManageProviderErrorMessage(
                manageProviderMutation.error,
              )}
              type="error"
              showIcon
            />
          </Form.Item>
        )}

        <Form.Item
          name="provider"
          label="Assign Provider"
          rules={[{ required: true, message: "Please select a provider" }]}
        >
          <Select
            placeholder="Select provider"
            options={providerOptions}
            onChange={() => {
              form.setFieldsValue({
                email: undefined,
                providerId: undefined,
                phoneNumber: undefined,
              });
            }}
          />
        </Form.Item>

        {isEmailProvider ? (
          <>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter an email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="user@example.com" />
            </Form.Item>
            {selectedProvider === UserProviderType.APPLE && (
              <Form.Item
                name="providerId"
                label="Provider ID"
                extra="Optional. Recommended for Apple - Apple only shares the user id on first sign-in."
              >
                <Input placeholder="SSO subject / Apple user id" />
              </Form.Item>
            )}
          </>
        ) : null}

        {selectedProvider === UserProviderType.PHONE ? (
          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter a phone number" },
              {
                pattern: validationRegExpr.phone,
                message: "Enter a valid E.164 phone number, e.g. +3197067291469",
              },
            ]}
            extra="Use E.164 format, e.g. +3197067291469"
          >
            <Input placeholder="+3197067291469" />
          </Form.Item>
        ) : null}

        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={manageProviderMutation.isLoading}
            disabled={!selectedProvider}
          >
            Save
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default ManageProviderModal;
